import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { OwnerInvoiceService, OwnerInvoiceListItem } from '../../services/owner-invoice.service';

@Component({
  selector: 'app-owner-invoices',
  standalone: true,
  imports: [FormsModule, CurrencyPipe, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './owner-invoices.component.html',
})
export class OwnerInvoicesComponent implements OnInit {
  private readonly invoices = inject(OwnerInvoiceService);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly rows = signal<OwnerInvoiceListItem[]>([]);
  readonly printingId = signal<string | null>(null);

  readonly dateFrom = signal('');
  readonly dateTo = signal('');

  /** Rows within the selected date range (inclusive); empty filters = all. */
  readonly filtered = computed(() => {
    const fromMs = this.dateFrom() ? new Date(this.dateFrom() + 'T00:00:00').getTime() : null;
    const toMs   = this.dateTo()   ? new Date(this.dateTo()   + 'T23:59:59').getTime() : null;
    return this.rows().filter(r => {
      if (fromMs !== null && r.issueDateMs < fromMs) return false;
      if (toMs !== null && r.issueDateMs > toMs) return false;
      return true;
    });
  });

  readonly totals = computed(() => {
    const f = this.filtered();
    return {
      count: f.length,
      net:   f.reduce((s, r) => s + r.netCents, 0),
      vat:   f.reduce((s, r) => s + r.vatCents, 0),
      irpf:  f.reduce((s, r) => s + r.irpfCents, 0),
      total: f.reduce((s, r) => s + r.totalCents, 0),
    };
  });

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  async reload(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    try {
      this.rows.set(await this.invoices.listMyInvoices());
    } catch (err) {
      this.loadError.set(this.formatError(err));
    } finally {
      this.loading.set(false);
    }
  }

  clearFilter(): void {
    this.dateFrom.set('');
    this.dateTo.set('');
  }

  euros(cents: number): number {
    return cents / 100;
  }

  exportCsv(): void {
    const rows = this.filtered();
    if (rows.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    this.invoices.downloadCsv(rows, `facturas-comision-${stamp}.csv`);
  }

  async print(row: OwnerInvoiceListItem): Promise<void> {
    this.printingId.set(row.id);
    try {
      const { html, invoiceNumber } = await this.invoices.getInvoiceHtml(row.id);
      this.invoices.openPrintWindow(html, invoiceNumber);
    } catch (err) {
      this.loadError.set(this.formatError(err));
    } finally {
      this.printingId.set(null);
    }
  }

  private formatError(err: unknown): string {
    const code = typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code?: string }).code)
      : '';
    if (code === 'functions/not-found' || code === 'functions/unavailable') {
      return 'El servicio de facturas no está disponible. Inténtalo más tarde.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Inicia sesión para ver tus facturas.';
    }
    return err instanceof Error ? err.message : 'No se pudieron cargar las facturas.';
  }
}
